import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Mic, ShieldCheck, Radar, Check, MessageSquare, Play, Wrench } from "lucide-react";

const DEMO_VIDEO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_quote-app-pwa/artifacts/ogoj2qav_Video%20Project%209.mp4";

const VideoDemo = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const start = () => { const v = ref.current; if (v) { v.play(); v.setAttribute("controls", "true"); } };
  return (
    <div className="relative rounded-[22px] overflow-hidden border border-border bg-onSurface shadow-xl">
      <video
        ref={ref}
        data-testid="demo-video"
        src={DEMO_VIDEO_URL}
        playsInline
        preload="metadata"
        className="w-full block max-h-[70vh] object-contain bg-onSurface"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {!playing && (
        <button
          data-testid="demo-video-play"
          onClick={start}
          aria-label="Play demo video"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/60 via-black/25 to-black/40 active:bg-black/30 transition-colors"
        >
          <span className="w-16 h-16 rounded-full bg-brandPrimary text-onBrand flex items-center justify-center shadow-lg shadow-black/30 active:scale-95 transition-transform">
            <Play size={28} className="ml-1" fill="currentColor" />
          </span>
          <span className="font-heading font-extrabold text-white text-sm drop-shadow">Watch the 60-second demo</span>
        </button>
      )}
    </div>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Benefit = ({ icon: Icon, title, sub }) => (
  <div className="flex items-start gap-4 bg-surface rounded-[16px] border border-border p-4">
    <div className="w-12 h-12 shrink-0 rounded-[14px] bg-brandPrimary/12 flex items-center justify-center">
      <Icon size={24} className="text-brandPrimary" />
    </div>
    <div>
      <p className="font-heading font-extrabold text-[15px] text-onSurface leading-snug">{title}</p>
      <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{sub}</p>
    </div>
  </div>
);

// ---- Big in-app mockups (crisp, on-brand recreations) ----
const PhoneFrame = ({ children, label }) => (
  <div className="relative">
    <div className="rounded-[34px] border-[6px] border-onSurface bg-onSurface shadow-2xl overflow-hidden">
      <div className="bg-surfaceSecondary">
        <div className="h-6 bg-onSurface flex items-center justify-center">
          <div className="w-16 h-1.5 bg-white/20 rounded-full" />
        </div>
        <div className="p-3">{children}</div>
      </div>
    </div>
    {label && <p className="text-center text-xs font-bold uppercase tracking-widest text-muted-foreground mt-3">{label}</p>}
  </div>
);

const RadarMock = () => (
  <div className="space-y-2.5">
    <div className="bg-onSurface text-onBrand rounded-[16px] p-4 relative overflow-hidden">
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/5" />
      <div className="flex items-center gap-1.5 mb-3 relative">
        <Radar size={15} className="text-brandPrimary" />
        <p className="font-heading font-extrabold text-[13px] tracking-wide">Money Radar</p>
      </div>
      <div className="grid grid-cols-2 gap-3 relative">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/60">Outstanding</p>
          <p className="font-heading text-2xl font-extrabold mt-0.5">£380</p>
          <p className="text-[10px] text-white/60">4 invoices</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/60">Overdue</p>
          <p className="font-heading text-2xl font-extrabold mt-0.5 text-brandPrimary">£220</p>
          <p className="text-[10px] text-white/60">2 need chasing</p>
        </div>
      </div>
    </div>
    {[["Shaun", "£150 · 2 days overdue", "INV-0016"], ["Ellis", "£70 · 2 days overdue", "INV-0017"]].map(([n, s, num]) => (
      <div key={num} className="bg-surface border border-border rounded-[12px] p-3 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-[13px] text-onSurface">{n}</p>
          <p className="text-[11px] font-semibold text-error truncate">{s}</p>
          <p className="text-[10px] text-muted-foreground">{num}</p>
        </div>
        <span className="shrink-0 bg-brandPrimary text-onBrand rounded-full px-3 h-8 text-[12px] font-heading font-bold flex items-center gap-1"><MessageSquare size={12} /> Chase</span>
      </div>
    ))}
  </div>
);

const ChaseMock = () => (
  <div className="space-y-2.5">
    <div className="flex items-center gap-2 px-1 pt-1">
      <div className="w-7 h-7 rounded-[8px] bg-brandTertiary flex items-center justify-center"><MessageSquare size={15} className="text-brandPrimary" /></div>
      <p className="font-heading font-extrabold text-[13px] text-onSurface">Chase for payment</p>
    </div>
    <div className="bg-surface border border-border rounded-[12px] p-3">
      <p className="text-[11px] font-semibold text-muted-foreground mb-1">To Shaun · INV-0016 · £150</p>
      <div className="bg-brandTertiary text-onSurface rounded-[14px] rounded-tl-sm p-3 text-[12px] leading-relaxed">
        Hi Shaun, just a friendly reminder that invoice INV-0016 for £150 is now 2 days overdue. You can pay by bank transfer whenever's easy — thanks! — Kai Waste
      </div>
    </div>
    <div className="bg-brandPrimary text-onBrand rounded-full h-10 flex items-center justify-center gap-1.5 text-[13px] font-heading font-bold"><MessageSquare size={14} /> Review &amp; Send</div>
    <div className="bg-surface border border-border rounded-full h-10 flex items-center justify-center gap-1.5 text-[13px] font-heading font-bold text-success"><Check size={14} /> Mark as paid</div>
  </div>
);

const CTA = ({ testid }) => (
  <button
    data-testid={testid}
    onClick={login}
    className="w-full bg-brandPrimary text-onBrand rounded-full h-14 font-heading font-extrabold text-base flex items-center justify-center gap-2 hover:bg-brandSecondary transition-colors active:scale-[0.98] shadow-lg shadow-brandPrimary/25"
  >
    Start 7-Day Free Trial <ArrowRight size={20} />
  </button>
);

export default function Welcome() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-surfaceSecondary flex justify-center">
      <div className="w-full max-w-md bg-surface flex flex-col px-6 pt-14 pb-32 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-brandTertiary/70 blur-2xl pointer-events-none" />

        {/* Hero */}
        <div className="relative animate-fade-up">
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/icon-192.png" alt="Docket" className="w-11 h-11 rounded-[12px]" />
            <span className="font-heading text-2xl font-extrabold tracking-tight text-onSurface">Docket</span>
          </div>
          <div className="inline-flex items-center gap-1.5 bg-brandPrimary/12 text-brandPrimary px-3 py-1 rounded-full text-xs font-bold mb-4">
            <Wrench size={13} /> Built for UK tradespeople
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-onSurface leading-[1.05]">
            Jobs done.<br /><span className="text-brandPrimary">Paperwork gone.</span>
          </h1>
          <p className="text-base text-muted-foreground mt-4 leading-relaxed max-w-sm">
            The no-faff admin app for tradespeople — professional quotes and invoices in seconds, plus an AI mate called Kai who chases your money.
          </p>
        </div>

        {/* Single CTA */}
        <div className="relative mt-7 animate-fade-up" style={{ animationDelay: "60ms" }}>
          <CTA testid="google-login-btn" />
          <p className="text-center text-xs text-muted-foreground mt-3">7-day free trial · then £12/month · cancel anytime</p>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs font-semibold text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>
          <button data-testid="welcome-email-signup" onClick={() => navigate("/auth", { state: { mode: "signup" } })} className="w-full bg-surface border border-borderStrong text-onSurface rounded-full h-[52px] font-heading font-bold text-sm flex items-center justify-center gap-2 hover:border-brandPrimary transition-colors active:scale-[0.98]">
            Sign up with email
          </button>
          <p className="text-center text-sm text-muted-foreground mt-4">Already have an account? <button data-testid="welcome-email-login" onClick={() => navigate("/auth", { state: { mode: "login" } })} className="font-bold text-brandPrimary">Log in</button></p>
        </div>

        {/* Demo video */}
        <div className="relative mt-10 animate-fade-up" style={{ animationDelay: "90ms" }}>
          <p className="text-center font-heading text-xl font-extrabold text-onSurface mb-1">See Docket in action</p>
          <p className="text-center text-sm text-muted-foreground mb-5">A quick run-through so you know exactly what you're getting.</p>
          <VideoDemo />
        </div>

        {/* 3 big benefits */}
        <div className="relative flex flex-col gap-3 mt-10 animate-fade-up" style={{ animationDelay: "120ms" }}>
          <Benefit icon={Mic} title="Speak the job → invoice in 20 seconds" sub="Just talk. Kai writes it up. Save 5 hours a week on admin." />
          <Benefit icon={ShieldCheck} title="Look the part on every job" sub="Branded, professional quotes & invoices as PDFs in seconds." />
          <Benefit icon={Radar} title="Kai chases who owes you" sub="Automatic, polite reminders that get you paid up to 3× faster." />
        </div>

        {/* Big app mockups */}
        <div className="relative mt-12 animate-fade-up" style={{ animationDelay: "160ms" }}>
          <p className="text-center font-heading text-xl font-extrabold text-onSurface mb-1">See exactly who owes you</p>
          <p className="text-center text-sm text-muted-foreground mb-6">Money Radar surfaces every unpaid job — one tap to chase.</p>
          <PhoneFrame label="Money Radar"><RadarMock /></PhoneFrame>
          <div className="h-10" />
          <PhoneFrame label="Chase my money"><ChaseMock /></PhoneFrame>
        </div>
      </div>

      {/* Sticky bottom CTA (same single action) */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-surface/95 backdrop-blur-md border-t border-border px-6 py-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
        <CTA testid="google-login-btn-sticky" />
      </div>
    </div>
  );
}
